import React, { FC, useState } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Pagination,
  Alert,
  Chip,
} from '@mui/material'
import { TrendingUp, TrendingDown } from '@mui/icons-material'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'
import { usePharmacyComparisonQuery } from '@/features/internal/metrics'
import { Dayjs } from 'dayjs'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

type SortField =
  | 'pharmacy_name'
  | 'average_duration'
  | 'soap_rate'
  | 'copy_rate'
  | 'average_exchange_count'
  | 'speech_rate'
  | 'total_count'
type SortOrder = 'asc' | 'desc'

const PAGE_SIZE = 20

interface ComparisonTabProps {
  startDate: Dayjs | null
  endDate: Dayjs | null
}

export const ComparisonTab: FC<ComparisonTabProps> = ({
  startDate,
  endDate,
}) => {
  const [sortField, setSortField] = useState<SortField>('average_duration')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [currentPage, setCurrentPage] = useState(1)

  const {
    data: comparisonData,
    isLoading: loading,
    error: queryError,
  } = usePharmacyComparisonQuery({
    params: {
      start_date: startDate?.format('YYYY-MM-DD') ?? '',
      end_date: endDate?.format('YYYY-MM-DD') ?? '',
      sort_by: sortField,
      sort_order: sortOrder,
      page: currentPage,
      page_size: PAGE_SIZE,
    },
    enabled: !!startDate && !!endDate,
  })

  const error = queryError?.message ?? null

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
    setCurrentPage(1)
  }

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page)
  }

  const formatDiff = (diff: string, isPercentage = false) => {
    const value = parseFloat(diff)
    const isPositive = value > 0
    const color = colorTokens.text.secondary // グレー色で統一

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          justifyContent: 'center',
        }}
      >
        {isPositive ? (
          <TrendingUp sx={{ fontSize: 14, color }} />
        ) : (
          <TrendingDown sx={{ fontSize: 14, color }} />
        )}
        <Typography variant="caption" sx={{ color }}>
          {diff}
          {isPercentage ? '%' : ''}
        </Typography>
      </Box>
    )
  }

  const getRankEmoji = (rank: number) => {
    switch (rank) {
      case 1:
        return '🥇'
      case 2:
        return '🥈'
      case 3:
        return '🥉'
      default:
        return ''
    }
  }

  return (
    <Box>
      {loading && <LoadingIndicator />}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 比較テーブル */}
      {comparisonData && (
        <Paper
          elevation={0}
          sx={{
            backgroundColor: colorTokens.background.paper,
            borderRadius: 2,
            border: `1px solid ${colorTokens.border.light}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            p: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: colorTokens.text.primary,
              }}
            >
              薬局別実績
            </Typography>
            <Typography variant="body2" color="textSecondary">
              全{comparisonData?.total_pharmacies || 0}薬局
            </Typography>
          </Box>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              mt: 2,
              border: `1px solid ${colorTokens.border.light}`,
              borderRadius: 1,
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'pharmacy_name'}
                      direction={
                        sortField === 'pharmacy_name' ? sortOrder : 'asc'
                      }
                      onClick={() => handleSort('pharmacy_name')}
                    >
                      薬局名
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === 'total_count'}
                      direction={
                        sortField === 'total_count' ? sortOrder : 'asc'
                      }
                      onClick={() => handleSort('total_count')}
                    >
                      来局数
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === 'soap_rate'}
                      direction={sortField === 'soap_rate' ? sortOrder : 'asc'}
                      onClick={() => handleSort('soap_rate')}
                    >
                      SOAP生成率
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === 'copy_rate'}
                      direction={sortField === 'copy_rate' ? sortOrder : 'asc'}
                      onClick={() => handleSort('copy_rate')}
                    >
                      コピー率(対SOAP)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === 'average_duration'}
                      direction={
                        sortField === 'average_duration' ? sortOrder : 'asc'
                      }
                      onClick={() => handleSort('average_duration')}
                    >
                      平均指導時間
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === 'average_exchange_count'}
                      direction={
                        sortField === 'average_exchange_count'
                          ? sortOrder
                          : 'asc'
                      }
                      onClick={() => handleSort('average_exchange_count')}
                    >
                      平均やりとり回数
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === 'speech_rate'}
                      direction={
                        sortField === 'speech_rate' ? sortOrder : 'asc'
                      }
                      onClick={() => handleSort('speech_rate')}
                    >
                      話速
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonData.pharmacies.map((pharmacy) => (
                  <TableRow key={pharmacy.pharmacy_id}>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {getRankEmoji(pharmacy.rank)}
                        <Typography variant="body2">
                          {pharmacy.pharmacy_name}
                        </Typography>
                        <Chip label={`${pharmacy.data_count}件`} size="small" />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {pharmacy.metrics.total_count}件
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {pharmacy.metrics.soap_rate.toFixed(1)}% (
                        {pharmacy.metrics.soap_count || 0}件)
                      </Typography>
                      {formatDiff(
                        pharmacy.diff_from_average.soap_rate_diff,
                        true
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {pharmacy.metrics.copy_rate?.toFixed(1) || 0}% (
                        {pharmacy.metrics.copy_count || 0}/
                        {pharmacy.metrics.soap_count || 0})
                      </Typography>
                      {formatDiff(
                        pharmacy.diff_from_average.copy_rate_diff || '0',
                        true
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {pharmacy.metrics.average_duration}秒
                      </Typography>
                      {formatDiff(pharmacy.diff_from_average.duration_diff)}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {pharmacy.metrics.average_exchange_count.toFixed(1)}回
                      </Typography>
                      {formatDiff(pharmacy.diff_from_average.exchange_diff)}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {pharmacy.metrics.speech_rate !== null
                          ? `${pharmacy.metrics.speech_rate.toFixed(0)}文字/分`
                          : '-'}
                      </Typography>
                      {pharmacy.metrics.speech_rate !== null &&
                        formatDiff(
                          pharmacy.diff_from_average.speech_rate_diff || '0'
                        )}
                    </TableCell>
                  </TableRow>
                ))}

                {/* 全社平均行 */}
                <TableRow sx={{ backgroundColor: colorTokens.grey[50] }}>
                  <TableCell>
                    <Typography variant="body2">━━ 全社平均</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">-</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {comparisonData.company_average.soap_rate.toFixed(1)}% (-)
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {comparisonData.company_average.copy_rate?.toFixed(1) ||
                        0}
                      % (-)
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {comparisonData.company_average.average_duration}秒
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {comparisonData.company_average.average_exchange_count.toFixed(
                        1
                      )}
                      回
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {comparisonData.company_average.speech_rate !== null
                        ? `${comparisonData.company_average.speech_rate.toFixed(
                            0
                          )}文字/分`
                        : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* ページネーション */}
          {comparisonData.total_pharmacies > PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={Math.ceil(comparisonData.total_pharmacies / PAGE_SIZE)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  )
}
